import * as React from 'react';
import ExecutionContext from '../../ExecutionContext';
import type { Facet } from '../../Search/Facet.csharp';
import type { SearchCriteria } from '../../Search/SearchCriteria.csharp';
import type { SelectedFacet } from '../../Search/SelectedFacet.csharp';
import { getBaseSearchQueryParameters, phraseParameterName } from '../PagedFetchData/SearchCriteriaUtils';
import { FacetsQueryParameterConverter } from './FacetsQueryParameterConverter';
import { changeQueryParametersInActiveUrl, getQueryParametersFromUrl } from './QueryParameterUtils';

interface SearchBaseProps {
    phrase: string;
    facets: Facet[];
    searchUrl: string;
    selectedFacets?: SelectedFacet[];
}

export interface SearchState {
    searchParams: SearchCriteria;
}

export abstract class SearchBase<P extends SearchBaseProps, S extends SearchState> extends React.Component<P, S> {
    private queryParameterConverter: FacetsQueryParameterConverter;

    constructor(props: P) {
        super(props);

        const { phrase, facets, selectedFacets } = this.props;

        this.state = {
            searchParams: {
                phrase: phrase || '',
                selectedFacets: this.initSelectedFacetState(facets, selectedFacets || []),
            },
        } as S;

        this.queryParameterConverter = new FacetsQueryParameterConverter(facets);
    }

    componentDidMount(): void {
        if (ExecutionContext.IsServerSide()) {
            return;
        }

        const searchCriteria: undefined | SearchCriteria = this.getBaseSearchCriteria(document.URL);

        if (!searchCriteria) {
            return;
        }

        if (searchCriteria.selectedFacets.length) {
            this.handleSearchParamsChange(searchCriteria);
        } else if (searchCriteria.phrase) {
            this.handleQueryChange(searchCriteria.phrase);
        }
    }

    componentDidUpdate(prevPros: SearchBaseProps, prevState: SearchState) {
        if (ExecutionContext.IsServerSide()) {
            return;
        }

        const { searchParams } = this.state;

        if (prevState.searchParams !== searchParams) {
            const queryParams = getBaseSearchQueryParameters(searchParams);
            const translatedQueryParams = queryParams.map(param =>
                this.queryParameterConverter.convertToUserReadableQueryParameter(param)
            );

            changeQueryParametersInActiveUrl(translatedQueryParams, false);
        }
    }

    initSelectedFacetState = (facets: Facet[], selectedFacets: SelectedFacet[]): SelectedFacet[] =>
        facets.map(facet => {
            const selectedFacet = selectedFacets.filter(sf => sf.id === facet.id);

            if (selectedFacet.length === 1) {
                return selectedFacet[0];
            }

            return {
                id: facet.id,
                values: [] as string[],
            };
        });

    protected getBaseSearchQueryParameters = (searchCriteria: SearchCriteria) =>
        getBaseSearchQueryParameters(searchCriteria);

    protected getBaseSearchCriteria = (url: string) => {
        const {
            searchParams: { selectedFacets },
        } = this.state;

        const queryParams = getQueryParametersFromUrl(url);

        if (queryParams.length === 0) {
            return undefined;
        }

        const phraseParam = queryParams.find(x => x.name === phraseParameterName);
        const phrase = phraseParam && phraseParam.value;
        const facetParams = queryParams.filter(x => x.name !== phraseParameterName);

        const selectedFacetsResult: SelectedFacet[] = facetParams.reduce((result: SelectedFacet[], item) => {
            const newItem = this.queryParameterConverter.convertFromUserReadableQueryParameter(item);
            const { name, value } = newItem;
            const facet = result.find(x => x.id === name);

            if (facet) {
                facet.values = [...facet.values, value];
            } else {
                result.push({ id: name, values: [value] });
            }

            return result;
        }, []);
        const emptyFacets = selectedFacets.filter(item => selectedFacetsResult.map(x => x.id).indexOf(item.id) === -1);
        const facets = selectedFacetsResult.concat(emptyFacets);

        return {
            phrase,
            selectedFacets: facets,
        } as SearchCriteria;
    };

    protected handleQueryChange = (newSearchValue: string) => {
        const { searchParams } = this.state;
        this.setState({
            searchParams: { ...searchParams, phrase: newSearchValue },
        });
    };

    protected handleFacetChange = (selectedFacets: SelectedFacet[]) => {
        const { searchParams } = this.state;
        this.setState({
            searchParams: { ...searchParams, selectedFacets },
        });
    };

    protected handleSearchParamsChange = (searchCriteria: SearchCriteria) => {
        this.setState({
            searchParams: searchCriteria,
        });
    };
}
